import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArchiveRestore, ArrowLeft, Trash2, Undo2 } from 'lucide-react';
import { useArchiveSubmission, useDeleteSubmission, useFormSubmission, useSaveChecklist } from '@/api/hooks';
import { ApiError } from '@/api/client';
import { submitErrorMessage } from '@/lib/formErrors';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ChecklistCells, useChecklistEdits } from '@/components/SubmissionChecklist';
import { DATE_MSG, ITEMS, LABEL, groupSubmissions, orderedClients, typeLabel } from '@/lib/submissions';

type Row = Record<string, unknown>;

/** Read-only label/value pair for the form summary. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm text-foreground">{children || '—'}</div>
    </div>
  );
}

/** One submitted request: the form as submitted (read-only) plus its admin checklist. */
export function SubmittedFormView() {
  const { id } = useParams();
  const notify = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  // Back to the list as it was (tab and filters), or the plain list on a direct link.
  const back = () => (location.key !== 'default' ? navigate(-1) : navigate('/forms/submitted'));
  const q = useFormSubmission(Number(id));
  const save = useSaveChecklist();
  const reopen = useArchiveSubmission();
  const del = useDeleteSubmission();
  const g = useMemo(() => groupSubmissions((q.data ?? []) as Row[])[0], [q.data]);
  const ce = useChecklistEdits();
  const [confirm, setConfirm] = useState<'submit' | 'delete' | null>(null);

  if (q.isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  if (!g) {
    return (
      <div className="space-y-3 p-6">
        <p className="text-sm text-muted-foreground">Request #{id} was not found.</p>
        <Link to="/forms/submitted" className="btn-secondary inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to Submitted Forms</Link>
      </div>
    );
  }

  const items = ITEMS[g.formType] ?? [];
  const isEng = g.formType === 'Discontinuation';
  const blocked = ce.missingDate([g]).length > 0;
  const effective = [...new Set(g.details.map((d) => d.effectiveDate).filter(Boolean))].join(', ');
  const rows = isEng ? g.details.map((d) => ({ d, isSub: false })) : orderedClients(g);

  // Same as Submit on the list: save, and the request moves to Completed once every row is done.
  async function onSubmit() {
    setConfirm(null);
    const out = ce.toSave([g]);
    if (!out.length) return;
    try {
      const res = await save.mutateAsync(out);
      ce.reset();
      notify('success', res?.completed?.includes(g.id)
        ? `Request #${g.id} moved to Completed and the submitter was emailed.`
        : `Saved ${out.length} row${out.length === 1 ? '' : 's'}.`);
    } catch (e) {
      notify('error', submitErrorMessage(e));
    }
  }

  async function onReopen() {
    try {
      await reopen.mutateAsync({ id: g.id, archived: false });
      notify('success', `Request #${g.id} reopened.`);
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Update failed');
    }
  }

  async function onDelete() {
    setConfirm(null);
    try {
      await del.mutateAsync(g.id);
      notify('success', `Request #${g.id} deleted.`);
      navigate('/forms/submitted');
    } catch (e) {
      notify('error', e instanceof ApiError ? e.problem.title : 'Delete failed');
    }
  }

  return (
    <div className="w-full max-w-6xl space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={back} className="btn-secondary inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Back to Submitted Forms</button>
        <div className="flex gap-2">
          {g.completed && (
            <button className="btn-secondary inline-flex items-center gap-1" disabled={reopen.isPending} onClick={() => void onReopen()}
              title="Reopen: back to the open list so the checklist can be changed">
              <ArchiveRestore className="h-4 w-4" /> Reopen
            </button>
          )}
          <button className="btn-secondary inline-flex items-center gap-1 text-destructive" disabled={del.isPending}
            onClick={() => setConfirm('delete')}>
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        </div>
      </div>

      {/* The form as submitted (read-only) */}
      <div className="card space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            #{g.id} · {typeLabel(g.formType)} Request
            {g.completed && <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-800">Completed</span>}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Submitted {g.date} by {g.by} ({g.email}) · {g.emailed ? 'admins notified' : 'email pending'}
            {g.completed && ` · completed ${g.completedAt}${g.completedBy ? ` by ${g.completedBy}` : ''}`}
          </p>
        </div>

        <div>
          <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{isEng ? 'Engagement(s)' : 'Client(s)'}</div>
          <div className="space-y-0.5 text-sm">
            {rows.map(({ d, isSub }) => (
              <div key={d.key} className={isSub ? 'pl-5 text-muted-foreground' : ''}>
                {isEng
                  ? <><b>{d.engagementId}</b> · {d.clientId} - {d.clientName}{d.service && ` · ${d.service}`}</>
                  : <>{isSub && '↳ '}{d.clientId} - {d.clientName}{isSub && <span className="italic"> (sub-client)</span>}
                    {d.newMaster && <span className="ml-1 text-muted-foreground">(new master: {d.newMaster})</span>}</>}
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label={g.formType === 'Termination' ? 'Effective Termination Date' : g.formType === 'Reactivation' ? 'Effective Reactivation Date' : 'Effective Discontinuation Date'}>
            {effective}
          </Field>
          {g.formType === 'Termination' && <Field label="Termination Reason">{g.termReason}</Field>}
          {g.formType === 'Termination' && <Field label="Disable Crunchafi Services">{g.crunchafi}</Field>}
        </div>
        <Field label="Notes">{g.notes}</Field>
      </div>

      {/* Admin checklist for this request */}
      <div className="card space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-foreground">Checklist</h3>
          {!g.completed && <div className="flex gap-2">
            <button className="btn-secondary inline-flex items-center gap-1" disabled={!ce.dirty || save.isPending}
              title="Discard unsaved checklist edits" onClick={ce.reset}>
              <Undo2 className="h-4 w-4" /> Discard
            </button>
            <button className="btn-success" disabled={!ce.dirty || blocked || save.isPending} title={blocked ? DATE_MSG : ''}
              onClick={() => setConfirm('submit')}>
              {save.isPending ? 'Submitting…' : 'Submit'}
            </button>
          </div>}
        </div>
        {g.completed && (
          <p className="rounded-md bg-secondary px-3 py-2 text-sm text-muted-foreground">
            This request is completed. Reopen it to make changes.
          </p>
        )}
        {g.details.length === 0 ? (
          <p className="text-sm text-muted-foreground">No client or engagement rows on this request.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-muted-foreground">
                  <th className="py-1 pr-3">{isEng ? 'Engagement' : 'Client'}</th>
                  {items.map((c) => <th key={c} className="py-1 pr-2">{LABEL[c]}</th>)}
                  <th className="py-1 pr-3">Date Completed</th>
                  <th className="py-1">Updated By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ d, isSub }) => (
                  <tr key={d.key} className={`border-t border-border/60 ${ce.edits[d.key] ? 'bg-warning/10' : ''}`}>
                    <td className={`py-1 pr-3 ${isSub ? 'pl-4' : ''}`}>
                      {isEng ? <><b>{d.engagementId}</b> · {d.clientName}</> : <>{isSub && '↳ '}{d.clientId} - {d.clientName}</>}
                    </td>
                    <ChecklistCells g={g} d={d} ce={ce} />
                    <td className="py-1 text-xs text-muted-foreground">{d.updatedBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!g.completed && (
          <p className="text-xs text-muted-foreground">
            Edited rows are highlighted until you Submit. The request moves to Completed (and its submitter is emailed) once every row is done.
          </p>
        )}
      </div>
      <ConfirmDialog
        open={confirm !== null}
        message={confirm === 'delete'
          ? `Delete request #${g.id} (${typeLabel(g.formType)}, submitted by ${g.by})? It will be removed from Submitted Forms. This cannot be undone from the app.`
          : 'Do you wish to submit these changes? If every row is done, the request will move to Completed and the submitter will be emailed.'}
        confirmLabel={confirm === 'delete' ? 'Delete' : 'Submit'}
        cancelLabel="Cancel"
        onConfirm={() => (confirm === 'delete' ? void onDelete() : void onSubmit())}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
